import React, { useCallback, useEffect, useState } from 'react';
import { Car, FileText, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { inspectionService } from '../../services/inspectionService';
import { useLanguage } from '../../hooks/useLanguage';
import { QuoteDetailsModal } from '../inspections/QuoteDetailsModel';
import { InspectionDetail } from '../Dashboard/InspectionDetail';
import { Appointment, formatMoney } from './appointmentUtils';

interface Props { appointment: Appointment; hideQuoteTotal?: boolean }

// Rappel de la demande dans le panneau d'un rendez-vous : véhicule, demande du client, pré-analyse IA,
// devis accepté. La fiche complète de l'inspection (statut en cours / terminée) s'ouvre à la demande.
export const InspectionRecap: React.FC<Props> = ({ appointment: a, hideQuoteTotal }) => {
    const { t, currentLanguage } = useLanguage();
    const navigate = useNavigate();
    const [inspection, setInspection] = useState<any>(null);
    const [loading, setLoading] = useState(!!a.inspectionId);
    const [error, setError] = useState(false);
    const [showQuote, setShowQuote] = useState(false);
    const [showDetail, setShowDetail] = useState(false);

    const load = useCallback(async () => {
        if (!a.inspectionId) return null;
        try {
            const insp = await inspectionService.getInspectionById(String(a.inspectionId));
            setInspection(insp); setError(false);
            return insp;
        } catch {
            setError(true);
            return null;
        } finally {
            setLoading(false);
        }
    }, [a.inspectionId]);

    useEffect(() => { load(); }, [load]);

    // Rendez-vous sans inspection liée (ne devrait pas arriver dans le parcours normal) : on montre ce qu'on a
    if (!a.inspectionId) {
        return (
            <section className="rounded-xl border border-indigo-100 bg-indigo-50/60 p-3 space-y-2">
                <p className="text-sm font-bold text-indigo-700">{t('appointments.recap.title')}</p>
                <p className="text-sm font-semibold text-gray-900 flex items-center gap-2"><Car size={16} className="text-indigo-500" />{[a.vehicleBrand, a.vehicleModel].filter(Boolean).join(' ') || '—'}</p>
                {!hideQuoteTotal && a.quoteSnapshot?.totalTTC != null && <p className="text-sm text-gray-700">{t('appointments.request.acceptedQuote')} : <span className="font-bold text-green-700">{formatMoney(a.quoteSnapshot.totalTTC, currentLanguage)}</span></p>}
                <p className="text-xs text-gray-500">{t('appointments.recap.noInspection')}</p>
            </section>
        );
    }

    if (!a.inspectionId) return null;

    const quoteId = (inspection?.quotes || []).find((q: any) => String(q.status).startsWith('accepted'))?.quoteId || a.quoteId;
    const ai = inspection?.aiAnalysis;
    const vehicle = inspection
        ? [inspection.vehicleBrand, inspection.vehicleModel, inspection.vehicleYear].filter(Boolean).join(' ')
        : [a.vehicleBrand, a.vehicleModel].filter(Boolean).join(' ');
    const extra = [inspection?.registration, inspection?.mileage ? `${inspection.mileage} km` : null, inspection?.fuelType, inspection?.transmission].filter(Boolean).join(' · ');

    const changeStatus = async (id: string, status: string) => {
        await inspectionService.updateInspectionStatus(id, status);
        await load();
    };

    return (
        <section className="rounded-xl border border-indigo-100 bg-indigo-50/60 p-3 space-y-3">
            <p className="text-sm font-bold text-indigo-700">{t('appointments.recap.title')}</p>

            {loading ? <p className="text-sm text-gray-500">{t('appointments.form.loadingSlots')}</p>
                : error ? <p className="text-sm text-red-700">{t('appointments.recap.error')}</p> : (<>
                    <div className="flex items-start gap-2 text-sm">
                        <Car size={16} className="mt-0.5 text-indigo-500 flex-shrink-0" />
                        <div><p className="font-semibold text-gray-900">{vehicle || '—'}</p>{extra && <p className="text-gray-500">{extra}</p>}</div>
                    </div>
                    {inspection?.description && (
                        <div className="text-sm"><p className="text-xs text-gray-500">{t('appointments.recap.clientRequest')}</p><p className="text-gray-800">« {inspection.description} »</p></div>
                    )}
                    <div className="rounded-lg bg-white p-2.5 text-sm">
                        <p className="flex items-center gap-1.5 font-semibold text-indigo-700"><Sparkles size={14} />{t('appointments.recap.aiTitle')}</p>
                        {ai ? (<>
                            <p className="text-gray-800 mt-1">{ai.summary}</p>
                            {(ai.detectedIssues || []).length > 0 && (
                                <ul className="mt-1 list-disc ps-5 text-gray-700">{ai.detectedIssues.slice(0, 5).map((i: string, idx: number) => <li key={idx}>{i}</li>)}</ul>
                            )}
                            {(ai.estimatedRepairs || ai.estimatedCostRange) && (
                                <p className="mt-1 text-xs text-gray-500">{[ai.estimatedRepairs, ai.estimatedCostRange].filter(Boolean).join(' · ')}</p>
                            )}
                        </>) : <p className="text-gray-500 mt-1">{t('appointments.recap.noAnalysis')}</p>}
                    </div>
                </>)}

            <div className="flex flex-wrap items-center justify-between gap-2">
                {!hideQuoteTotal ? (
                    <div className="text-sm">
                        <p className="text-xs text-gray-500">{t('appointments.request.acceptedQuote')}</p>
                        <p className="font-bold text-green-700">{formatMoney(a.quoteSnapshot?.totalTTC, currentLanguage)}</p>
                    </div>
                ) : <span />}
                <div className="flex flex-wrap gap-2">
                    {quoteId && <button onClick={() => setShowQuote(true)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-indigo-300 bg-white text-indigo-700 text-sm font-semibold hover:bg-indigo-50"><FileText size={14} />{t('appointments.recap.viewQuote')}</button>}
                    {inspection && <button onClick={() => setShowDetail(true)} className="px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700">{t('appointments.recap.openInspection')}</button>}
                </div>
            </div>

            {showQuote && quoteId && <QuoteDetailsModal quoteId={String(quoteId)} onClose={() => setShowQuote(false)} />}
            {showDetail && inspection && (
                <InspectionDetail
                    inspection={inspection}
                    onClose={() => { setShowDetail(false); load(); }}
                    onStartChat={() => navigate('/dashboard/messages')}
                    onCreateQuote={() => undefined}
                    onStatusChange={changeStatus}
                    onInspectionUpdated={async () => { await load(); }}
                />
            )}
        </section>
    );
};
